/**
 * Timeline — Vertical chronological events
 *
 * Enhanced from v1 with connecting line, date badges,
 * alternating left/right on desktop, and per-event citations.
 */
import React from 'react';
import type { TimelineData } from '../types';

interface TimelineProps {
  data: TimelineData;
}

export const Timeline: React.FC<TimelineProps> = ({ data }) => {
  const { events } = data;

  if (!events || events.length === 0) return null;

  return (
    <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/20 shadow-[0_0_20px_rgba(34,211,238,0.08)] p-4 md:p-6">
      <div className="relative">
        {/* Vertical connecting line */}
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/30 via-cyan-400/15 to-transparent md:-translate-x-px" />

        <div className="space-y-8">
          {events.map((event, idx) => {
            const isLeft = idx % 2 === 0;

            return (
              <div
                key={idx}
                className="relative group"
              >
                {/* Desktop: alternating layout */}
                <div
                  className={`flex items-start gap-4 md:gap-8 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Mobile: content is always right of the line */}
                  {/* Desktop: content alternates sides */}
                  <div
                    className={`hidden md:block flex-1 ${
                      isLeft ? 'text-right' : 'text-left'
                    }`}
                  >
                    <div
                      className={`inline-block max-w-sm transition-all duration-300 group-hover:translate-y-[-1px]`}
                    >
                      {/* Date badge (desktop, opposite side) */}
                      <span className="inline-block px-2.5 py-0.5 mb-2 text-[10px] font-bold uppercase tracking-wider rounded-full bg-cyan-400/10 text-cyan-400/70 border border-cyan-400/15">
                        {event.date}
                      </span>
                      <h4 className="text-sm font-bold text-cyan-300 mb-1">
                        {event.title}
                      </h4>
                      {event.description && (
                        <p className="text-xs text-cyan-300/60 leading-relaxed">
                          {event.description}
                        </p>
                      )}
                      {/* Citations */}
                      {event.citations && event.citations.length > 0 && (
                        <div className={`flex gap-1 mt-2 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                          {event.citations.map((c) => (
                            <span
                              key={c}
                              className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold rounded-full bg-cyan-400/15 text-cyan-400/60"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="relative z-10 flex-shrink-0 flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full bg-black border-2 border-cyan-400/40 shadow-[0_0_12px_rgba(34,211,238,0.2)] group-hover:border-cyan-400/70 group-hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all duration-300">
                    {event.icon ? (
                      <span className="text-sm">{event.icon}</span>
                    ) : (
                      <div className="w-2.5 h-2.5 rounded-full bg-cyan-400/60 group-hover:bg-cyan-400 transition-colors duration-300" />
                    )}
                  </div>

                  {/* Content (mobile: always shown; desktop: hidden because alternate side shows it) */}
                  <div className="flex-1 md:hidden min-w-0">
                    <span className="inline-block px-2.5 py-0.5 mb-2 text-[10px] font-bold uppercase tracking-wider rounded-full bg-cyan-400/10 text-cyan-400/70 border border-cyan-400/15">
                      {event.date}
                    </span>
                    <h4 className="text-sm font-bold text-cyan-300 mb-1">
                      {event.title}
                    </h4>
                    {event.description && (
                      <p className="text-xs text-cyan-300/60 leading-relaxed">
                        {event.description}
                      </p>
                    )}
                    {event.citations && event.citations.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {event.citations.map((c) => (
                          <span
                            key={c}
                            className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold rounded-full bg-cyan-400/15 text-cyan-400/60"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Desktop: empty placeholder on the other side */}
                  <div className="hidden md:block flex-1" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
